import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { MessageSquare, ShieldOff, PhoneOff } from "lucide-react";
import SmsThread from "@/components/SmsThread";

/**
 * CustomerCommsPanel — reusable SMS drawer wrapping SmsThread.
 *
 * Behaviour:
 *   • Opens a right-side drawer on desktop (`sm:max-w-xl`), which Radix
 *     Sheet turns into a full-screen overlay on narrow viewports — close
 *     enough to a bottom-sheet for the mobile cases we see in the field.
 *   • Trigger is disabled with a tooltip (title attribute) when the customer
 *     has no phone or has opted out via Text Request's STOP state.
 *   • Record IDs (jobId/invoiceId/estimateId) propagate to the composer so
 *     TemplatePicker can resolve estimate_link / invoice_link / job_* fields.
 *   • Mark-read syncing with the Overview unread widget is handled by the
 *     existing SmsThread → GET /{scope}/messages → implicit mark-read path.
 */
export default function CustomerCommsPanel({
  customerId = null,
  leadId = null,
  scope = "customer",
  customer = null,            // optional {phone, sms_opted_out} for disabled state
  jobId = null,
  invoiceId = null,
  estimateId = null,
  renderTrigger = null,
  triggerLabel = "Text",
  triggerSize = "sm",
  defaultOpen = false,
  disabled = false,
  disabledReason = "",
  testId = "customer-comms-trigger",
}) {
  const [open, setOpen] = useState(defaultOpen);
  const id = scope === "customer" ? customerId : leadId;
  if (!id) return null;

  // Compute disabled state from the customer prop when caller didn't force it.
  let isDisabled = disabled;
  let reason = disabledReason;
  let DisabledIcon = ShieldOff;
  if (!isDisabled && customer) {
    if (customer.sms_opted_out) {
      isDisabled = true;
      reason = "Customer has opted out of SMS (reply START to opt in)";
      DisabledIcon = ShieldOff;
    } else if (!customer.phone) {
      isDisabled = true;
      reason = "No phone number on file for this customer";
      DisabledIcon = PhoneOff;
    }
  }

  const trigger = renderTrigger
    ? renderTrigger({ open: () => !isDisabled && setOpen(true), disabled: isDisabled, reason })
    : (
      <Button
        type="button"
        variant="outline"
        size={triggerSize}
        onClick={() => !isDisabled && setOpen(true)}
        className="border-zinc-200 hover:border-[#FF5A00] hover:text-[#FF5A00] disabled:opacity-40 disabled:cursor-not-allowed"
        disabled={isDisabled}
        title={isDisabled ? reason : "Open SMS thread"}
        data-testid={testId}
      >
        {isDisabled
          ? <DisabledIcon className="w-4 h-4 mr-1 text-rose-500"/>
          : <MessageSquare className="w-4 h-4 mr-1"/>}
        {triggerLabel}
      </Button>
    );

  return (
    <>
      {trigger}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-xl flex flex-col p-0 gap-0"
          data-testid="customer-comms-panel"
        >
          <SheetHeader className="px-4 pt-4 pb-2 border-b border-zinc-100 shrink-0">
            <SheetTitle className="flex items-center gap-2 text-base">
              <MessageSquare className="w-4 h-4 text-[#FF5A00]"/>
              Customer SMS
            </SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto p-4">
            <SmsThread
              scope={scope}
              id={id}
              jobId={jobId}
              invoiceId={invoiceId}
              estimateId={estimateId}
            />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
